import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import BackToTop from "@/components/app/BackToTop";
import NotificationBell from "@/components/app/NotificationBell";
import { ScanLine, LayoutDashboard, Camera, Shield, LogOut, FolderKanban, Share2, Eye, X, Sun, Moon, BarChart3 } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout, impersonating, stopImpersonate } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const navCls = ({ isActive }) =>
    `px-3 py-1.5 text-sm font-medium transition-colors border-b-2 ${
      isActive ? "border-[#002FA7] text-[#0B0F19]" : "border-transparent text-gray-500 hover:text-[#0B0F19]"
    }`;

  return (
    <div className="min-h-screen bg-[var(--ra-bg)] text-[var(--ra-text)]">
      <BackToTop />      {impersonating && (
        <div className="bg-amber-400 text-[#0B0F19] relative z-[60]" data-testid="impersonation-banner">
          <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Eye size={16} />
              <span className="text-sm font-semibold">
                Mode admin — vous consultez le compte de <span className="font-bold">{impersonating.name}</span> ({impersonating.email})
              </span>
            </div>
            <button className="text-sm font-semibold underline hover:no-underline flex items-center gap-1" onClick={stopImpersonate} data-testid="stop-impersonate-button">
              <X size={14} /> Quitter le mode admin
            </button>
          </div>
        </div>
      )}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b border-[var(--ra-border)]" style={{ background: "var(--ra-header-bg)" }}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
            <div className="w-7 h-7 bg-[#002FA7] flex items-center justify-center">
              <ScanLine className="text-white" size={16} />
            </div>
            <span className="font-heading font-black text-lg tracking-tighter hidden sm:inline">FACTURÉ</span>
          </Link>
          <nav className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto scrollbar-hide" data-testid="main-nav">
            <NavLink to="/dashboard" className={navCls} data-testid="nav-dashboard">
              <LayoutDashboard size={14} className="inline sm:mr-1" /> <span className="hidden sm:inline">Tableau</span>
            </NavLink>
            <NavLink to="/reports" className={navCls} data-testid="nav-reports">
              <FolderKanban size={14} className="inline sm:mr-1" /> <span className="hidden sm:inline">Rapports</span>
            </NavLink>
            <NavLink to="/stats" className={navCls} data-testid="nav-stats">
              <BarChart3 size={14} className="inline sm:mr-1" /> <span className="hidden sm:inline">Stats</span>
            </NavLink>
            <NavLink to="/scan" className={navCls} data-testid="nav-scan">
              <Camera size={14} className="inline sm:mr-1" /> <span className="hidden sm:inline">Scanner</span>
            </NavLink>
            <NavLink to="/shares" className={navCls} data-testid="nav-shares">
              <Share2 size={14} className="inline sm:mr-1" /> <span className="hidden sm:inline">Partages</span>
            </NavLink>
            {user?.role === "vp" && (
              <NavLink to="/vp/review" className={navCls} data-testid="nav-vp">
                <Shield size={14} className="inline sm:mr-1" /> <span className="hidden sm:inline">Approbations</span>
              </NavLink>
            )}
            {user?.role === "admin" && (
              <NavLink to="/admin" className={navCls} data-testid="nav-admin">
                <Shield size={14} className="inline sm:mr-1" /> <span className="hidden sm:inline">Admin</span>
              </NavLink>
            )}
          </nav>
          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            {user && <NotificationBell />}
            <button
              onClick={toggle}
              className="btn-ghost"
              title={theme === "dark" ? "Mode clair" : "Mode sombre"}
              data-testid="theme-toggle"
              aria-label="Basculer le thème"
            >
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            {user && (
              <div className="flex items-center gap-2">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-[var(--ra-border)]" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[var(--ra-bg-muted)]" />
                )}
                <div className="hidden md:block text-right leading-tight">
                  <div className="text-xs font-semibold">{user.name}</div>
                  <div className="overline" style={{ fontSize: 9 }}>{user.role}</div>
                </div>
              </div>
            )}
            <button onClick={logout} className="btn-ghost" data-testid="logout-button" title="Déconnexion">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-3 sm:px-6 md:px-8 py-6 sm:py-8">{children}</main>
    </div>
  );
}
